import { useLazyQuery } from '@apollo/client'
import React, { useEffect, useState } from 'react'
import BaseDialog from '../../../../../../components/dialog'
import { notify } from '../../../../../../components/toastify'
import { STATUS } from '../../../../../../constants/messages'
import { CHECK_DATA_EXIST_TO_GENERATE_JOB } from '../../../../../../queries/adminPortal'
import { IDataForGenerateJob, IDataSearchInput } from '../../interface'
import CheckDataExistBeforeGenerateJob from './checkDataExistBeforeGenerateJob'
import ConfirmGenerateJobDialog from './confirmGenerateJobDialog'

interface Props {
  modalIsOpen: boolean
  handleCloseDialog?: (val: boolean) => void
  refetchEmailTable?: () => void
}

const styleDialog = {
  content: {
    width: '90%',
    maxWidth: '800px',
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    transform: 'translate(-50%, -50%)',
    borderRadius: '16px',
    boxShadow: '8px 11px 21px 0px rgba(148,144,144,0.75)',
    background: '#fdfdfd',
  },
}

const GenerateJobDialog: React.FC<Props> = ({
  modalIsOpen,
  handleCloseDialog = () => null,
  refetchEmailTable = () => null,
}) => {
  const [currentStep, setCurrentStep] = useState<number>(1)
  const [customStyle, setCustomStyle] = useState(styleDialog)
  const [dataForGenerateJob, setDataForGenerateJob] = useState<IDataForGenerateJob>({})
  const [dataSearchInput, setDataSearchInput] = useState<IDataSearchInput>({
    applicationNumber: '',
    carrierBondNumber: '',
  })

  useEffect(() => {
    const style = { ...styleDialog }
    switch (currentStep) {
      case 1:
        style.content.maxWidth = '500px'
        setCustomStyle(style)
        break
      case 2:
        style.content.maxWidth = '750px'
        setCustomStyle(style)
        break
      default:
        setCustomStyle(style)
        break
    }
  }, [currentStep])

  const [checkDataExistToGenerateJob, { loading: loadingCheckData }] = useLazyQuery(
    CHECK_DATA_EXIST_TO_GENERATE_JOB,
    { fetchPolicy: 'no-cache' },
  )

  const validateSearchInput = (applicationNumber: string, carrierBondNumber: string) => {
    if (applicationNumber === '' || carrierBondNumber === '') {
      return false
    }
    return true
  }

  const handleNextBtn = async (applicationNumber: string, carrierBondNumber: string) => {
    if (!validateSearchInput(applicationNumber, carrierBondNumber)) {
      notify('Missing data to process', STATUS.ERROR)
      return
    }

    if (carrierBondNumber.length < 6) {
      notify('Min length of Bond number is 6', STATUS.WARNING)
      return
    }

    const response = await checkDataExistToGenerateJob({
      variables: {
        applicationNumber,
        carrierBondNumber,
      },
      fetchPolicy: 'no-cache',
    })

    const dataResponse: IDataForGenerateJob = response.data.checkDataExistToGenerateJob

    if (dataResponse) {
      if (!dataResponse.messageId) {
        notify(`No data for this information`, STATUS.INFO)
      } else {
        setDataForGenerateJob(dataResponse)
        setCurrentStep(2)
      }
    } else {
      notify('Something wrong', STATUS.ERROR)
      setCurrentStep(1)
    }
  }

  const handleBackBtn = async (
    applicationNumber: string,
    carrierBondNumber: string,
    step: number,
  ) => {
    setDataSearchInput({ ...dataSearchInput, applicationNumber, carrierBondNumber })
    setCurrentStep(step)
  }

  const render = () => {
    switch (currentStep) {
      case 1:
        return (
          <CheckDataExistBeforeGenerateJob
            handleCloseDialog={handleCloseDialog}
            handleNextBtn={handleNextBtn}
            loadingCheckData={loadingCheckData}
            dataSearchInput={dataSearchInput}
          />
        )
      case 2:
        return (
          <ConfirmGenerateJobDialog
            handleCloseDialog={handleCloseDialog}
            data={dataForGenerateJob}
            handleBackBtn={(applicationNumber, carrierBondNumber, step) =>
              handleBackBtn(applicationNumber, carrierBondNumber, step)
            }
            refetchEmailTable={refetchEmailTable}
          />
        )
      default:
        return <div></div>
    }
  }

  return (
    <BaseDialog modalIsOpen={modalIsOpen} customStyles={customStyle}>
      {render()}
    </BaseDialog>
  )
}

export default GenerateJobDialog
